import { Box, ClickAwayListener, Popper } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import useEventListener from '../../hooks/useEventListener.hooks';
import { IssueReportRecord } from '../../redux/features/issueReport/issueReport.props';
import Colors from '../../utils/color';
import { focusOmniBar } from '../../utils/omniBarFocus';
import IssueBacklogList from './IssueBacklogList';
import { ISSUE_TOOLTIP_Z_INDEX } from './issueReportLayers';

/** Clear of the badge, so opening this does not cover the thing that opened it. */
const POPPER_MODIFIERS = [{ name: 'offset', options: { offset: [0, 10] } }];

interface IssueBacklogPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  reports: IssueReportRecord[];
  /** The pointer entered the popper — the host cancels its pending close. */
  onPointerEnter: () => void;
  /** The pointer left the popper — the host starts its close delay. */
  onPointerLeave: () => void;
  /** Click-away or Escape. Closes for good, latch included. */
  onDismiss: () => void;
  /** A row was opened, so a hover-out must no longer close this. */
  onLatch: () => void;
}

/**
 * HITL-1574: the outstanding backlog, hanging off the Issue button's badge.
 *
 * ## A Popper, not a Tooltip
 *
 * The button already has a Tooltip and it is `disableInteractive` on purpose — it
 * floats over the action bar and would otherwise block the controls beneath it. This
 * surface is the opposite: it has to be ENTERABLE (the agent moves the pointer onto
 * it) and LATCHABLE (they click a row and it stays), which a tooltip cannot be without
 * becoming a pointer trap over the ordering controls.
 *
 * Two hover surfaces on one control is the trap HITL-1501 documented — `mouseover`
 * bubbles, so a tooltip on a child fires the parent's too and the agent gets both at
 * once. Avoided structurally rather than arbitrated: the badge that opens this is a
 * SIBLING of the Tooltip's child, not inside it, so hovering the badge never reaches
 * the button's own hover listener. The host also disables that listener while this is
 * open, as belt and braces.
 *
 * ## Leave-intent, not leave
 *
 * Closing on `mouseleave` alone made the list unusable: the pointer has to cross a few
 * pixels of gap to get from a 16px badge onto the list. So the host runs a short close
 * delay and this cancels it on enter.
 *
 * ## Latched rows are dismissed only deliberately
 *
 * Clicking a row expands the classifier, and from then on only a click-away or Escape
 * closes this. Hover-dismissal would pull the classification section out from under an
 * agent mid-sentence. `IssueClassifyBody`'s unmount commit would still save the text,
 * but the surface vanishing while they type is wrong regardless of what survives.
 *
 * ## Caret custody
 *
 * `data-issue-report` opts the whole subtree out of agent-interception detection —
 * classifying is supervisory, and without it every keystroke in a row's detail field
 * would raise the take-over prompt. `data-caret-owner` stops the omnibar reclaiming
 * the caret from underneath an open row. Both are on the ROOT because the guards match
 * with `closest()` from the real event target, which is a chip's text node or the
 * input, never this element.
 *
 * The marker needs no new entry in `AGENT_INTERCEPTION_IGNORED_SELECTORS`:
 * `ISSUE_REPORT_SELECTOR` is already in that list, and that is exactly what carrying
 * the same marker buys.
 */
const IssueBacklogPopover = ({
  anchorEl,
  open,
  reports,
  onPointerEnter,
  onPointerLeave,
  onDismiss,
  onLatch,
}: IssueBacklogPopoverProps) => {
  /**
   * Stamped when the popover opens, so the ages hold still while they are read. A
   * render-time `Date.now()` would also make every row re-render on any parent
   * update with a different age.
   */
  const [openedAtMs, setOpenedAtMs] = useState(() => Date.now());
  /**
   * Whether a row was ever opened while this was up. Only then does closing hand the
   * caret back — a hover that never took the caret must not move it, or brushing the
   * badge would yank the agent out of whatever they were typing.
   */
  const tookCaretRef = useRef(false);

  useEffect(() => {
    if (open) {
      setOpenedAtMs(Date.now());
      return undefined;
    }
    // Closed. Hand the caret back only if this ever held it, deferred a tick so the
    // popper's DOM has finished tearing down — focusing while the focused node is
    // being removed leaves the caret on <body> anyway.
    if (tookCaretRef.current) {
      tookCaretRef.current = false;
      setTimeout(focusOmniBar, 0);
    }
    return undefined;
  }, [open]);

  /**
   * WINDOW CAPTURE with `stopPropagation`, the same shape the flag panel uses and for
   * the same reason: focus is usually in the omnibar, so a React handler on this
   * subtree would never see the press, and letting Escape through would also trigger
   * Autocomplete's `clearOnEscape` and wipe a half-typed order.
   *
   * Stands down while a dialog is in the DOM so it cannot hold Escape hostage from
   * the take-over prompt. The flag panel's handler cannot collide with this one — the
   * host does not render this while a flag window is open.
   */
  const handleEscape = (event: Event) => {
    if (!open) return;
    const e = event as KeyboardEvent;
    if (e.key !== 'Escape') return;
    if (
      typeof document !== 'undefined' &&
      document.querySelector('[role="dialog"]')
    ) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onDismiss();
  };

  useEventListener('keydown', handleEscape, window, true);

  const handleRowExpand = () => {
    tookCaretRef.current = true;
    onLatch();
  };

  return (
    <Popper
      open={open && Boolean(anchorEl) && reports.length > 0}
      anchorEl={anchorEl}
      // UPWARD, and right-aligned with the button. The action bar sits at the bottom
      // of the console, so downward would open off-screen — and opening above means
      // the pointer travelling from the badge to the list never crosses the button,
      // which is what keeps the button's own tooltip out of the way.
      placement="top-end"
      modifiers={POPPER_MODIFIERS}
      // `style`, not `sx`: this MUI version's base Popper does not take `sx`. Same
      // layering rule as the panel's tooltips — see issueReportLayers.
      style={{ zIndex: ISSUE_TOOLTIP_Z_INDEX }}
    >
      <ClickAwayListener onClickAway={onDismiss}>
        <Box
          data-issue-report="true"
          data-caret-owner="true"
          data-testid="issue-backlog-popover"
          onMouseEnter={onPointerEnter}
          onMouseLeave={onPointerLeave}
          sx={{
            // 860 for the same reason the flag panel is 860: it is the width at which
            // the widest chip row fits without wrapping, and the taxonomy must be
            // readable in one glance.
            width: 'min(860px, calc(100vw - 32px))',
            // The height cap bounds the number of ROWS, never the chip grid — a row
            // expands to its full height inside a container that scrolls as a whole,
            // so no part of the taxonomy is ever hidden behind a gesture. Without a
            // cap, 50 retained reports would run off the top of the screen.
            maxHeight: 'calc(100vh - 160px)',
            overflowY: 'auto',
            backgroundColor: Colors.daintree,
            color: Colors.white,
            // The badge's red, not the flag panel's green. The panel's green belongs
            // to its tick and means "captured, nothing more to do"; this surface is
            // the opposite state, and a red badge that opens a green-edged card would
            // contradict itself inside one gesture.
            borderLeft: `4px solid ${Colors.amaranth}`,
            p: 1.5,
            boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
          }}
        >
          <IssueBacklogList
            reports={reports}
            // Named after the control the agent used, not after the data: they
            // hovered the Issue button's badge.
            surface="issue_button"
            nowMs={openedAtMs}
            onRowExpand={handleRowExpand}
          />
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};

export default IssueBacklogPopover;
